import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ChevronRight, MapPin, User, Package, CreditCard, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { formatCOP } from '@/utils/format';
import { ordersAPI, paymentsAPI, shippingAPI, couponsAPI } from '@/utils/api';
import { MyCouponsPicker } from '@/components/cart/MyCouponsPicker';
import { CouponUpsell } from '@/components/cart/CouponUpsell';
import { toast } from 'sonner';

const departments = ['Cundinamarca', 'Antioquia', 'Valle', 'Atlántico', 'Santander', 'Bolívar', 'Nariño', 'Otro'];
const documentTypes = [
  { value: 'CC', label: 'Cédula de ciudadanía' },
  { value: 'CE', label: 'Cédula de extranjería' },
  { value: 'NIT', label: 'NIT (empresa)' },
  { value: 'PP', label: 'Pasaporte' },
];

const Checkout = () => {
  const { items, subtotal, totalWeight, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_document: '',
    customer_document_type: 'CC',
    billing_name: '',
    dian_regime: '',
    department: '',
    city: '',
    address: '',
    neighborhood: '',
    notes: '',
    receiver: '',
    accept_terms: false
  });

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null); // {code, discount_amount, discount_type, discount_value}
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [myCoupons, setMyCoupons] = useState([]);

  useEffect(() => {
    if (!user) { setMyCoupons([]); return; }
    couponsAPI.mine()
      .then((data) => setMyCoupons(data.filter((c) => c.status === 'active')))
      .catch(() => setMyCoupons([]));
  }, [user]);

  // Pre-fill form when user is authenticated
  useEffect(() => {
    if (user && user.email) {
      setForm(prev => ({
        ...prev,
        customer_name: prev.customer_name || user.name || '',
        customer_email: prev.customer_email || user.email || '',
        customer_phone: prev.customer_phone || user.phone || ''
      }));
    }
  }, [user]);

  const [shippingCost, setShippingCost] = useState(0);
  const [isFree, setIsFree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);

  // Stable per-tab session id so we upsert the same abandoned-cart row
  const [sessionId] = useState(() => {
    let sid = sessionStorage.getItem('npg_cart_sid');
    if (!sid) {
      sid = (crypto?.randomUUID?.() || `sid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      sessionStorage.setItem('npg_cart_sid', sid);
    }
    return sid;
  });

  // Debounced save of the in-progress cart for the abandoned-cart reminder
  useEffect(() => {
    const email = form.customer_email?.trim();
    if (!email || !/.+@.+\..+/.test(email) || items.length === 0) return;
    const handler = setTimeout(() => {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      fetch(`${BACKEND}/api/carts/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          email,
          customer_name: form.customer_name || null,
          items: items.map((i) => ({
            product_id: i.product_id,
            product_name: i.product_name,
            variant_id: i.variant_id,
            sku: i.sku,
            size: i.size,
            price: i.price,
            quantity: i.quantity,
            image: i.image || null,
          })),
          subtotal,
          currency: 'COP',
        }),
      }).catch(() => { /* silent */ });
    }, 1500);
    return () => clearTimeout(handler);
  }, [form.customer_email, form.customer_name, items, subtotal, sessionId]);

  useEffect(() => {
    if (items.length === 0) {
      navigate('/perros');
    }
  }, [items, navigate]);

  useEffect(() => {
    if (form.department && form.city && items.length > 0) {
      calculateShipping();
    }
  }, [form.department, form.city]);

  const calculateShipping = async () => {
    setCalculating(true);
    try {
      const res = await shippingAPI.calculate({
        department: form.department,
        city: form.city,
        total_weight_kg: totalWeight,
        order_total: subtotal
      });
      setShippingCost(res.cost);
      setIsFree(res.is_free);
    } catch (e) {
      console.error(e);
    } finally {
      setCalculating(false);
    }
  };

  const total = Math.max(0, subtotal + shippingCost - (couponApplied?.discount_amount || 0));

  const applyCoupon = async (codeOverride) => {
    const code = (typeof codeOverride === 'string' ? codeOverride : couponCode).trim().toUpperCase();
    if (!code) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const data = await couponsAPI.validate(code, subtotal);
      setCouponApplied(data);
      setCouponCode(data.code);
      toast.success(`Cupón ${data.code} aplicado`);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Cupón inválido';
      setCouponError(typeof detail === 'string' ? detail : 'Cupón inválido');
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const clearCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
    setCouponError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) {
      toast.error('Debes aceptar los términos y política de tratamiento de datos');
      return;
    }

    setLoading(true);
    try {
      // Create order
      const order = await ordersAPI.create({
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        customer_document: form.customer_document,
        customer_document_type: form.customer_document_type,
        billing_name: form.billing_name || null,
        dian_regime: form.dian_regime || null,
        coupon_code: couponApplied?.code || null,
        items: items.map((i) => ({
          product_id: i.product_id,
          product_name: i.product_name,
          variant_id: i.variant_id,
          sku: i.sku,
          size: i.size,
          price: i.price,
          quantity: i.quantity,
          subtotal: i.price * i.quantity
        })),
        subtotal,
        shipping_cost: shippingCost,
        discount: couponApplied?.discount_amount || 0,
        total,
        shipping_address: {
          department: form.department,
          city: form.city,
          address: form.address,
          neighborhood: form.neighborhood,
          receiver: form.receiver
        },
        notes: form.notes
      });

      // Create Bold hosted payment link and redirect
      const payment = await paymentsAPI.create({
        order_id: order.id,
        customer_email: form.customer_email
      });

      if (!payment?.checkout_url) {
        throw new Error('No se recibió URL de pago');
      }

      toast.success('Redirigiendo a Bold para completar el pago...');
      clearCart();
      // Full page navigation to Bold's hosted checkout
      window.location.assign(payment.checkout_url);
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === 'string' ? detail : 'Error al procesar el pedido. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
      <div className="mb-8">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Checkout</p>
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight">Finaliza tu compra</h1>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        <div className="lg:col-span-2 min-w-0 space-y-6">
          {/* Customer info */}
          <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6">
            <h2 className="font-display font-semibold text-lg text-[#1C1917] mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#2A5C43]" /> Información personal
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nombre completo</Label>
                <Input
                  required
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  data-testid="checkout-name"
                />
              </div>
              <div>
                <Label>Correo electrónico</Label>
                <Input
                  required
                  type="email"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  data-testid="checkout-email"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  required
                  type="tel"
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  data-testid="checkout-phone"
                />
              </div>
              <div>
                <Label>Tipo de documento</Label>
                <Select value={form.customer_document_type} onValueChange={(v) => setForm({ ...form, customer_document_type: v })}>
                  <SelectTrigger data-testid="checkout-doc-type"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {documentTypes.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{form.customer_document_type === 'NIT' ? 'NIT *' : 'Documento (opcional)'}</Label>
                <Input
                  value={form.customer_document}
                  onChange={(e) => setForm({ ...form, customer_document: e.target.value })}
                  data-testid="checkout-document"
                />
              </div>
              {form.customer_document_type === 'NIT' && (
                <>
                  <div className="sm:col-span-2">
                    <Label>Razón social *</Label>
                    <Input
                      value={form.billing_name}
                      onChange={(e) => setForm({ ...form, billing_name: e.target.value })}
                      placeholder="Nombre legal de la empresa"
                      data-testid="checkout-billing-name"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Régimen tributario</Label>
                    <Select value={form.dian_regime} onValueChange={(v) => setForm({ ...form, dian_regime: v })}>
                      <SelectTrigger data-testid="checkout-dian-regime"><SelectValue placeholder="Selecciona régimen" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="simple">Régimen Simple</SelectItem>
                        <SelectItem value="comun">Régimen Común (IVA)</SelectItem>
                        <SelectItem value="no-responsable">No responsable de IVA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Shipping */}
          <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6">
            <h2 className="font-display font-semibold text-lg text-[#1C1917] mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#2A5C43]" /> Dirección de entrega
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Departamento</Label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger data-testid="checkout-department"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {departments.map((d) => (
                      <SelectItem key={d} value={d.toLowerCase()}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ciudad</Label>
                <Input
                  required
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="checkout-city"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Dirección</Label>
                <Input
                  required
                  placeholder="Calle 123 #45-67"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="checkout-address"
                />
              </div>
              <div>
                <Label>Barrio</Label>
                <Input
                  value={form.neighborhood}
                  onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
                  data-testid="checkout-neighborhood"
                />
              </div>
              <div>
                <Label>Persona que recibe</Label>
                <Input
                  value={form.receiver}
                  onChange={(e) => setForm({ ...form, receiver: e.target.value })}
                  data-testid="checkout-receiver"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Indicaciones adicionales (opcional)</Label>
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="checkout-notes"
                />
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6">
            <div className="flex items-start gap-3">
              <Checkbox
                id="accept-terms"
                checked={form.accept_terms}
                onCheckedChange={(v) => setForm({ ...form, accept_terms: v })}
                data-testid="checkout-terms"
              />
              <Label htmlFor="accept-terms" className="text-sm text-[#57534E] leading-relaxed cursor-pointer">
                He leído y acepto los <a href="/terminos" className="text-[#2A5C43] underline">términos y condiciones</a> y la <a href="/privacidad" className="text-[#2A5C43] underline">política de tratamiento de datos personales</a>.
              </Label>
            </div>
          </div>
        </div>

        {/* Order summary */}
        <div className="lg:col-span-1 min-w-0">
          <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 sticky top-28">
            <h2 className="font-display font-semibold text-lg text-[#1C1917] mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-[#2A5C43]" /> Resumen del pedido
            </h2>
            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
              {items.map((item) => (
                <div key={item.variant_id} className="flex gap-3 text-sm">
                  <div className="w-14 h-14 rounded-lg bg-[#F7F2E8] shrink-0 overflow-hidden">
                    {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1C1917] line-clamp-2 leading-tight text-xs">{item.product_name}</p>
                    <p className="text-xs text-[#57534E]">{item.size} × {item.quantity}</p>
                  </div>
                  <p className="text-xs font-semibold text-[#2A5C43]">{formatCOP(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-[#E7E5E4] pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#57534E]">Subtotal</span>
                <span className="text-[#1C1917] font-medium" data-testid="checkout-subtotal">{formatCOP(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#57534E]">Envío</span>
                <span className="text-[#1C1917] font-medium" data-testid="checkout-shipping">
                  {calculating ? '...' : isFree ? 'Gratis' : shippingCost > 0 ? formatCOP(shippingCost) : 'Calcula al ingresar dirección'}
                </span>
              </div>
              {couponApplied && (
                <div className="flex justify-between text-[#2A5C43]" data-testid="checkout-discount-row">
                  <span className="flex items-center gap-1">
                    <span>Cupón</span>
                    <span className="text-xs bg-[#F7F2E8] px-2 py-0.5 rounded-full font-semibold">{couponApplied.code}</span>
                  </span>
                  <span className="font-medium">-{formatCOP(couponApplied.discount_amount)}</span>
                </div>
              )}
              <div className="border-t border-[#E7E5E4] pt-2 flex justify-between items-baseline">
                <span className="font-display font-semibold text-[#1C1917]">Total</span>
                <span className="font-display font-bold text-2xl text-[#2A5C43]" data-testid="checkout-total">{formatCOP(total)}</span>
              </div>
            </div>

            {/* Coupon code */}
            <div className="mt-4 pt-4 border-t border-[#F5F5F4]">
              {couponApplied ? (
                <div className="flex items-center justify-between bg-[#F7F2E8] rounded-full px-4 py-2" data-testid="checkout-coupon-applied">
                  <span className="text-xs font-semibold text-[#2A5C43]">✓ {couponApplied.code} aplicado</span>
                  <button type="button" onClick={clearCoupon} className="text-xs text-[#D47A4A] hover:underline" data-testid="checkout-coupon-remove">Quitar</button>
                </div>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Código de cupón"
                      className="uppercase text-sm"
                      data-testid="checkout-coupon-input"
                    />
                    <Button
                      type="button"
                      onClick={() => applyCoupon()}
                      disabled={couponLoading || !couponCode.trim()}
                      variant="outline"
                      className="rounded-full border-[#2A5C43] text-[#2A5C43] shrink-0"
                      data-testid="checkout-coupon-apply"
                    >
                      {couponLoading ? '...' : 'Aplicar'}
                    </Button>
                  </div>
                  {couponError && <p className="text-xs text-red-600 mt-2" data-testid="checkout-coupon-error">{couponError}</p>}
                  {user && <MyCouponsPicker coupons={myCoupons} subtotal={subtotal} onPick={applyCoupon} disabled={couponLoading} />}
                  {user && <CouponUpsell coupons={myCoupons} subtotal={subtotal} items={items} />}
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || !form.accept_terms}
              className="w-full mt-6 bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full py-6 font-semibold"
              data-testid="checkout-submit"
            >
              {loading ? 'Procesando...' : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" /> Pagar con Bold
                </>
              )}
            </Button>

            <p className="text-[11px] text-[#57534E] text-center mt-3">
              Pago procesado de forma segura por Bold Colombia
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
